// How much of a chart to draw at a size — the rule BI tools use for small
// tiles: as the chart shrinks, drop the axis titles first, then the tick
// labels, then the legend, so the plot keeps its room; below a minimum, draw
// nothing and say so. One rule for every chart (Custom charts and the builtin
// ones), dependency-free so the canvas can apply it before loading Vega.

export interface ChartSize {
  /** The chart's drawable width in pixels (0: not measured yet). */
  width: number;
  /** The chart's drawable height in pixels (0: not measured yet). */
  height: number;
}

export interface ChartDetail {
  /** False: too small to draw anything legible — show "enlarge" instead. */
  drawable: boolean;
  legend: boolean;
  /** Axis tick labels (and value labels on marks). */
  tickLabels: boolean;
  axisTitles: boolean;
}

/** The smallest [width, height] at which each element still earns its space. */
const MINIMUMS = {
  drawable: [80, 56],
  legend: [120, 72],
  tickLabels: [160, 100],
  axisTitles: [240, 150],
} as const;

function fits(size: ChartSize, [width, height]: readonly [number, number]) {
  return size.width >= width && size.height >= height;
}

/** How much of a chart to draw at `size`. */
export function chartDetailForSize(size: ChartSize): ChartDetail {
  // Before the first measure, draw in full rather than flash "too small".
  if (size.width <= 0 || size.height <= 0) {
    return { drawable: true, legend: true, tickLabels: true, axisTitles: true };
  }
  return {
    drawable: fits(size, MINIMUMS.drawable),
    legend: fits(size, MINIMUMS.legend),
    tickLabels: fits(size, MINIMUMS.tickLabels),
    axisTitles: fits(size, MINIMUMS.axisTitles),
  };
}
